"use client";

/**
 * ChatbotFolderTree Component
 *
 * Displays chatbot folders and chatbots in a tree structure with drag-and-drop support.
 * Similar to FolderTree but for chatbot organization.
 */
import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { DndContext, DragOverlay, pointerWithin, PointerSensor, useSensor, useSensors, useDroppable, DragStartEvent, DragEndEvent, DragOverEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import type { Chatbot, ChatbotFolderWithChildren } from "@/types/chatbot";
import { ChatbotFolderItem } from "./ChatbotFolderItem";
import { ChatbotItem } from "./ChatbotItem";
type ChatbotFolderTreeProps = {
  folders: ChatbotFolderWithChildren[];
  chatbots: Chatbot[]; // Root-level chatbots (without folders)
  selectedChatbotId: string | null;
  onSelectChatbot: (id: string) => void;
  onDeleteChatbot: (id: string) => Promise<void>;
  onRenameChatbot: (id: string, newName: string) => Promise<void>;
  onCreateFolder: (input: {
    name: string;
    parent_id?: string | null;
  }) => Promise<any>;
  onUpdateFolder: (id: string, updates: {
    name?: string;
    color?: string;
    is_collapsed?: boolean;
  }) => Promise<boolean | void>;
  onDeleteFolder: (id: string) => Promise<boolean | void>;
  onMoveFolder: (folderId: string, newParentId: string | null) => Promise<boolean | void>;
  onMoveChatbot: (chatbotId: string, folderId: string | null) => Promise<boolean | void>;
  onToggleFolderCollapse: (folderId: string) => Promise<boolean | void>;
  onStartChatbotThread?: (chatbotId: string, chatbotName: string) => void;
  onEditChatbot?: (id: string) => void;
  onDuplicateChatbot?: (id: string) => void;
};
type DragItem = {
  type: "folder" | "chatbot";
  id: string;
};
type DropIndicatorState = {
  folderName: string;
  x: number;
  y: number;
  itemType?: "folder" | "chatbot";
} | null;
export function ChatbotFolderTree(t0) {
  const $ = _c(102);
  const {
    folders,
    chatbots,
    selectedChatbotId,
    onSelectChatbot,
    onDeleteChatbot,
    onRenameChatbot,
    onCreateFolder,
    onUpdateFolder,
    onDeleteFolder,
    onMoveFolder,
    onMoveChatbot,
    onToggleFolderCollapse,
    onStartChatbotThread,
    onEditChatbot,
    onDuplicateChatbot
  } = t0;
  const [activeItem, setActiveItem] = useState(null);
  const [overId, setOverId] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderParentId, setNewFolderParentId] = useState(null);
  const newFolderInputRef = useRef(null);
  let findFolderName;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    findFolderName = (id, folderList) => {
      for (const folder of folderList) {
        if (folder.id === id) {
          return folder.name;
        }
        const found = findFolderName(id, folder.children);
        if (found) {
          return found;
        }
      }
      return null;
    };
    $[0] = findFolderName;
  } else {
    findFolderName = $[0];
  }
  const isDescendantOf = _temp;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      activationConstraint: {
        distance: 8
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sensors = useSensors(useSensor(PointerSensor, t1));
  let t2;
  let t3;
  if ($[2] !== isCreatingFolder) {
    t2 = () => {
      if (isCreatingFolder && newFolderInputRef.current) {
        newFolderInputRef.current.focus();
      }
    };
    t3 = [isCreatingFolder];
    $[2] = isCreatingFolder;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event => {
      const {
        active
      } = event;
      const itemType = active.data.current?.type as "folder" | "chatbot";
      setActiveItem({
        type: itemType,
        id: active.id as string
      });
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleDragStart = t4;
  let t5;
  if ($[6] !== folders) {
    t5 = event_0 => {
      const {
        over,
        active: active_0
      } = event_0;
      const newOverId = over?.id as string | null;
      setOverId(newOverId);
      const activeType = active_0.data.current?.type as "folder" | "chatbot";
      const overType = over?.data.current?.type as "folder" | "chatbot" | "root" | undefined;
      const getMousePosition = () => {
        const activatorEvent = event_0.activatorEvent as MouseEvent | TouchEvent;
        let x = 0;
        let y = 0;
        if (activatorEvent instanceof MouseEvent) {
          x = activatorEvent.clientX;
          y = activatorEvent.clientY;
        } else {
          if (activatorEvent instanceof TouchEvent && activatorEvent.touches[0]) {
            x = activatorEvent.touches[0].clientX;
            y = activatorEvent.touches[0].clientY;
          }
        }
        if (event_0.delta) {
          x = x + event_0.delta.x;
          y = y + event_0.delta.y;
        }
        return {
          x,
          y
        };
      };
      if (activeType === "chatbot" && overType === "folder" && newOverId) {
        const folderName = findFolderName(newOverId, folders);
        if (folderName) {
          const {
            x: x_0,
            y: y_0
          } = getMousePosition();
          setDropIndicator({
            folderName,
            x: x_0,
            y: y_0,
            itemType: "chatbot"
          });
        } else {
          setDropIndicator(null);
        }
      } else {
        if (activeType === "folder" && overType === "folder" && newOverId && active_0.id !== newOverId) {
          const activeFolderId = active_0.id as string;
          if (isDescendantOf(newOverId, activeFolderId, folders)) {
            setDropIndicator(null);
            return;
          }
          const targetFolderName = findFolderName(newOverId, folders);
          if (targetFolderName) {
            const {
              x: x_1,
              y: y_1
            } = getMousePosition();
            setDropIndicator({
              folderName: targetFolderName,
              x: x_1,
              y: y_1,
              itemType: "folder"
            });
          } else {
            setDropIndicator(null);
          }
        } else {
          if (overType === "root" || newOverId === "root") {
            setDropIndicator(null);
          } else {
            setDropIndicator(null);
          }
        }
      }
    };
    $[6] = folders;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleDragOver = t5;
  let t6;
  if ($[8] !== folders || $[9] !== onMoveChatbot || $[10] !== onMoveFolder) {
    t6 = async event_1 => {
      const {
        active: active_1,
        over: over_0
      } = event_1;
      setActiveItem(null);
      setOverId(null);
      setDropIndicator(null);
      if (!over_0) {
        return;
      }
      const activeType_0 = active_1.data.current?.type as "folder" | "chatbot";
      const overType_0 = over_0.data.current?.type as "folder" | "chatbot" | "root";
      const targetFolderId = over_0.id as string;
      const isRootTarget = overType_0 === "root" || over_0.id === "root";
      if (activeType_0 === "chatbot") {
        const activeChatbotId = active_1.id as string;
        if (overType_0 === "folder") {
          await onMoveChatbot(activeChatbotId, targetFolderId);
        } else {
          if (isRootTarget) {
            await onMoveChatbot(activeChatbotId, null);
          }
        }
      } else {
        if (activeType_0 === "folder") {
          const activeFolderId_0 = active_1.id as string;
          if (overType_0 === "folder" && active_1.id !== over_0.id) {
            if (isDescendantOf(targetFolderId, activeFolderId_0, folders)) {
              return;
            }
            await onMoveFolder(activeFolderId_0, targetFolderId);
          } else {
            if (isRootTarget) {
              await onMoveFolder(activeFolderId_0, null);
            }
          }
        }
      }
    };
    $[8] = folders;
    $[9] = onMoveChatbot;
    $[10] = onMoveFolder;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleDragEnd = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = async t8 => {
      const parentId = t8 === undefined ? null : t8;
      setNewFolderParentId(parentId);
      setIsCreatingFolder(true);
      setNewFolderName("");
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleCreateFolder = t7;
  let t8;
  if ($[13] !== newFolderName || $[14] !== newFolderParentId || $[15] !== onCreateFolder) {
    t8 = async () => {
      const trimmedName = newFolderName.trim();
      if (!trimmedName) {
        setIsCreatingFolder(false);
        return;
      }
      await onCreateFolder({
        name: trimmedName,
        parent_id: newFolderParentId
      });
      setIsCreatingFolder(false);
      setNewFolderName("");
      setNewFolderParentId(null);
    };
    $[13] = newFolderName;
    $[14] = newFolderParentId;
    $[15] = onCreateFolder;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleSaveNewFolder = t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsCreatingFolder(false);
      setNewFolderName("");
      setNewFolderParentId(null);
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleCancelNewFolder = t9;
  let t10;
  if ($[18] !== handleSaveNewFolder) {
    t10 = e => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSaveNewFolder();
      } else {
        if (e.key === "Escape") {
          handleCancelNewFolder();
        }
      }
    };
    $[18] = handleSaveNewFolder;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleNewFolderKeyDown = t10;
  let t11;
  if ($[20] !== chatbots || $[21] !== folders) {
    t11 = () => {
      const ids = [];
      const collectIds = folder_2 => {
        ids.push(folder_2.id);
        folder_2.chatbots.forEach(c => ids.push(c.id));
        folder_2.children.forEach(collectIds);
      };
      folders.forEach(collectIds);
      chatbots.forEach(c_0 => ids.push(c_0.id));
      return ids;
    };
    $[20] = chatbots;
    $[21] = folders;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const getAllIds = t11;
  const findActiveFolder = (id_1, folders_0) => {
    for (const folder_3 of folders_0) {
      if (folder_3.id === id_1) {
        return folder_3;
      }
      const found_1 = findActiveFolder(id_1, folder_3.children);
      if (found_1) {
        return found_1;
      }
    }
    return null;
  };
  const findActiveChatbot = _temp2;
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      id: "root",
      data: {
        type: "root"
      }
    };
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const {
    setNodeRef: setRootRef
  } = useDroppable(t12);
  const T0 = DndContext;
  const t13 = pointerWithin;
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => handleCreateFolder(null);
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <button type="button" onClick={t14} className="mb-2 flex items-center gap-2 px-2 py-1.5 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 rounded-md transition-colors"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>+ New Folder</button>;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== handleNewFolderKeyDown || $[27] !== handleSaveNewFolder || $[28] !== isCreatingFolder || $[29] !== newFolderName || $[30] !== newFolderParentId) {
    t16 = isCreatingFolder && newFolderParentId === null && <div className="mb-2 flex items-center gap-1 px-2 py-1"><svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg><input ref={newFolderInputRef} type="text" value={newFolderName} onChange={e_0 => setNewFolderName(e_0.target.value)} onKeyDown={handleNewFolderKeyDown} onBlur={handleSaveNewFolder} placeholder="Folder name" className="flex-1 rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500" /></div>;
    $[26] = handleNewFolderKeyDown;
    $[27] = handleSaveNewFolder;
    $[28] = isCreatingFolder;
    $[29] = newFolderName;
    $[30] = newFolderParentId;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== getAllIds) {
    t17 = getAllIds();
    $[32] = getAllIds;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== folders || $[35] !== handleNewFolderKeyDown || $[36] !== handleSaveNewFolder || $[37] !== isCreatingFolder || $[38] !== newFolderName || $[39] !== newFolderParentId || $[40] !== onDeleteChatbot || $[41] !== onDeleteFolder || $[42] !== onDuplicateChatbot || $[43] !== onEditChatbot || $[44] !== onRenameChatbot || $[45] !== onSelectChatbot || $[46] !== onStartChatbotThread || $[47] !== onToggleFolderCollapse || $[48] !== onUpdateFolder || $[49] !== overId || $[50] !== selectedChatbotId) {
    t18 = folders.map(folder_5 => <ChatbotFolderItem key={folder_5.id} folder={folder_5} depth={0} selectedChatbotId={selectedChatbotId} onSelectChatbot={onSelectChatbot} onDeleteChatbot={onDeleteChatbot} onRenameChatbot={onRenameChatbot} onUpdateFolder={onUpdateFolder} onDeleteFolder={onDeleteFolder} onToggleFolderCollapse={onToggleFolderCollapse} onCreateSubfolder={handleCreateFolder} isOver={overId === folder_5.id} isCreatingSubfolder={isCreatingFolder && newFolderParentId === folder_5.id} newFolderName={newFolderName} onNewFolderNameChange={setNewFolderName} onNewFolderKeyDown={handleNewFolderKeyDown} onNewFolderBlur={handleSaveNewFolder} newFolderInputRef={newFolderInputRef} onStartChatbotThread={onStartChatbotThread} onEditChatbot={onEditChatbot} onDuplicateChatbot={onDuplicateChatbot} />);
    $[34] = folders;
    $[35] = handleNewFolderKeyDown;
    $[36] = handleSaveNewFolder;
    $[37] = isCreatingFolder;
    $[38] = newFolderName;
    $[39] = newFolderParentId;
    $[40] = onDeleteChatbot;
    $[41] = onDeleteFolder;
    $[42] = onDuplicateChatbot;
    $[43] = onEditChatbot;
    $[44] = onRenameChatbot;
    $[45] = onSelectChatbot;
    $[46] = onStartChatbotThread;
    $[47] = onToggleFolderCollapse;
    $[48] = onUpdateFolder;
    $[49] = overId;
    $[50] = selectedChatbotId;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== chatbots || $[53] !== onDeleteChatbot || $[54] !== onDuplicateChatbot || $[55] !== onEditChatbot || $[56] !== onRenameChatbot || $[57] !== onSelectChatbot || $[58] !== onStartChatbotThread || $[59] !== selectedChatbotId) {
    let t20;
    if ($[61] !== onDeleteChatbot || $[62] !== onDuplicateChatbot || $[63] !== onEditChatbot || $[64] !== onRenameChatbot || $[65] !== onSelectChatbot || $[66] !== onStartChatbotThread || $[67] !== selectedChatbotId) {
      t20 = chatbot_0 => <ChatbotItem key={chatbot_0.id} chatbot={chatbot_0} isSelected={chatbot_0.id === selectedChatbotId} onClick={() => onSelectChatbot(chatbot_0.id)} onStartThread={onStartChatbotThread ? () => onStartChatbotThread(chatbot_0.id, chatbot_0.name) : undefined} onEdit={onEditChatbot ? () => onEditChatbot(chatbot_0.id) : undefined} onDuplicate={onDuplicateChatbot ? () => onDuplicateChatbot(chatbot_0.id) : undefined} onDelete={() => onDeleteChatbot(chatbot_0.id)} onRename={newName => onRenameChatbot(chatbot_0.id, newName)} depth={0} isDraggable={true} />;
      $[61] = onDeleteChatbot;
      $[62] = onDuplicateChatbot;
      $[63] = onEditChatbot;
      $[64] = onRenameChatbot;
      $[65] = onSelectChatbot;
      $[66] = onStartChatbotThread;
      $[67] = selectedChatbotId;
      $[68] = t20;
    } else {
      t20 = $[68];
    }
    t19 = chatbots.map(t20);
    $[52] = chatbots;
    $[53] = onDeleteChatbot;
    $[54] = onDuplicateChatbot;
    $[55] = onEditChatbot;
    $[56] = onRenameChatbot;
    $[57] = onSelectChatbot;
    $[58] = onStartChatbotThread;
    $[59] = selectedChatbotId;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[69] !== t17 || $[70] !== t18 || $[71] !== t19) {
    t20 = <SortableContext items={t17} strategy={verticalListSortingStrategy}>{t18}{t19}</SortableContext>;
    $[69] = t17;
    $[70] = t18;
    $[71] = t19;
    $[72] = t20;
  } else {
    t20 = $[72];
  }
  let t21;
  if ($[73] !== chatbots.length || $[74] !== folders.length || $[75] !== isCreatingFolder) {
    t21 = folders.length === 0 && chatbots.length === 0 && !isCreatingFolder && <div className="px-2 py-3 text-xs text-slate-500 text-center">No chatbots yet. Create one to get started.</div>;
    $[73] = chatbots.length;
    $[74] = folders.length;
    $[75] = isCreatingFolder;
    $[76] = t21;
  } else {
    t21 = $[76];
  }
  let t22;
  if ($[77] !== setRootRef || $[78] !== t16 || $[79] !== t20 || $[80] !== t21) {
    t22 = <div ref={setRootRef} className="flex flex-col min-h-[100px]">{t15}{t16}{t20}{t21}</div>;
    $[77] = setRootRef;
    $[78] = t16;
    $[79] = t20;
    $[80] = t21;
    $[81] = t22;
  } else {
    t22 = $[81];
  }
  const T1 = DragOverlay;
  const t23 = activeItem && activeItem.type === "folder" && <div className="rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-slate-200 shadow-lg"><div className="flex items-center gap-2"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>{findActiveFolder(activeItem.id, folders)?.name || "Folder"}</div></div>;
  let t24;
  if ($[82] !== activeItem || $[83] !== chatbots || $[84] !== findActiveChatbot || $[85] !== folders) {
    t24 = activeItem && activeItem.type === "chatbot" && <div className="rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-slate-200 shadow-lg"><div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-slate-500" />{findActiveChatbot(activeItem.id, folders, chatbots)?.name || "Chatbot"}.chatbot</div></div>;
    $[82] = activeItem;
    $[83] = chatbots;
    $[84] = findActiveChatbot;
    $[85] = folders;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== T1 || $[88] !== t23 || $[89] !== t24) {
    t25 = <T1>{t23}{t24}</T1>;
    $[87] = T1;
    $[88] = t23;
    $[89] = t24;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  let t26;
  if ($[91] !== dropIndicator) {
    t26 = dropIndicator && <div className="fixed z-[9999] pointer-events-none px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium shadow-lg whitespace-nowrap" style={{
      left: dropIndicator.x + 20,
      top: dropIndicator.y - 10
    }}><div className="flex items-center gap-1.5">{dropIndicator.itemType === "folder" ? <><svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>Move into "{dropIndicator.folderName}"</> : <><svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>Add to "{dropIndicator.folderName}"</>}</div></div>;
    $[91] = dropIndicator;
    $[92] = t26;
  } else {
    t26 = $[92];
  }
  let t27;
  if ($[93] !== T0 || $[94] !== handleDragEnd || $[95] !== handleDragOver || $[96] !== sensors || $[97] !== t13 || $[98] !== t22 || $[99] !== t25 || $[100] !== t26) {
    t27 = <T0 sensors={sensors} collisionDetection={t13} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>{t22}{t25}{t26}</T0>;
    $[93] = T0;
    $[94] = handleDragEnd;
    $[95] = handleDragOver;
    $[96] = sensors;
    $[97] = t13;
    $[98] = t22;
    $[99] = t25;
    $[100] = t26;
    $[101] = t27;
  } else {
    t27 = $[101];
  }
  return t27;
}
function _temp2(id_2, folders_1, rootChatbots) {
  const rootChatbot = rootChatbots.find(c_1 => c_1.id === id_2);
  if (rootChatbot) {
    return rootChatbot;
  }
  const searchFolders = folders_2 => {
    for (const folder_4 of folders_2) {
      const chatbot = folder_4.chatbots.find(c_2 => c_2.id === id_2);
      if (chatbot) {
        return chatbot;
      }
      const found_2 = searchFolders(folder_4.children);
      if (found_2) {
        return found_2;
      }
    }
    return null;
  };
  return searchFolders(folders_1);
}
function _temp(targetId, folderId, folderList_0) {
  const findFolder = (id_0, list) => {
    for (const folder_0 of list) {
      if (folder_0.id === id_0) {
        return folder_0;
      }
      const found_0 = findFolder(id_0, folder_0.children);
      if (found_0) {
        return found_0;
      }
    }
    return null;
  };
  const sourceFolder = findFolder(folderId, folderList_0);
  if (!sourceFolder) {
    return false;
  }
  const checkDescendants = folder_1 => {
    if (folder_1.id === targetId) {
      return true;
    }
    return folder_1.children.some(checkDescendants);
  };
  return checkDescendants(sourceFolder);
}